import { useCallback, useState } from 'react';

import { useNewsletterModal, NewsletterTopicPreselection } from '@/providers/NewsletterModalProvider';

/**
 * Hook for managing newsletter topic subscriptions via the proxy API.
 * Centralizes fetch logic, loading states, and status refresh.
 */
export function useNewsletterSubscription(cinemaId?: number) {
    const { refreshSubscriberStatus } = useNewsletterModal();
    const [isLoading, setIsLoading] = useState(false);

    const addTopic = useCallback(
        async (topic: NewsletterTopicPreselection) => {
            if (!cinemaId) return;

            setIsLoading(true);
            try {
                await fetch('/api/newsletter/subscription', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        cinema: cinemaId,
                        type: topic.type,
                        referenceId: topic.referenceId ?? null
                    })
                });
                refreshSubscriberStatus();
            } catch {
                // Silently fail — status refresh will show current state
            } finally {
                setIsLoading(false);
            }
        },
        [cinemaId, refreshSubscriberStatus]
    );

    const removeTopic = useCallback(
        async (type: string, referenceId?: string | null) => {
            if (!cinemaId) return;

            setIsLoading(true);
            try {
                await fetch('/api/newsletter/subscription', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        cinema: cinemaId,
                        type,
                        referenceId: referenceId ?? null
                    })
                });
                refreshSubscriberStatus();
            } catch {
                // Silently fail
            } finally {
                setIsLoading(false);
            }
        },
        [cinemaId, refreshSubscriberStatus]
    );

    return { addTopic, removeTopic, isLoading };
}
